import type { BootError } from '#~/depot/types.js';

export type ErrorClass = 'definitive' | 'transient' | 'offline';

export function classifyFetchError(error: unknown, response?: Response): ErrorClass {
  if (!navigator.onLine) return 'offline';

  if (response) {
    if (response.status === 401 || response.status === 403) return 'definitive';
    if (response.status >= 500) return 'transient';
  }

  if (error instanceof TypeError) return 'transient';

  return 'transient';
}

export function errorMessageForClass(cls: ErrorClass): string {
  switch (cls) {
    case 'offline':
      return 'No internet connection';
    case 'definitive':
      return 'Session expired. Please sign in again.';
    case 'transient':
      return 'Connection failed. Please try again.';
  }
}

export async function withBootRetry<T>(
  fn: () => Promise<T>,
  opts: {
    maxRetries: number;
    delayMs: number;
    setError: (error: BootError) => void;
    clearError: () => void;
    classify?: (error: unknown) => ErrorClass;
  }
): Promise<T> {
  const { maxRetries, delayMs, setError, clearError, classify = classifyFetchError } = opts;
  let lastError: unknown;
  let lastClass: ErrorClass = 'transient';

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();
      clearError();
      return result;
    } catch (err) {
      lastError = err;
      lastClass = classify(err);

      if (lastClass === 'offline' || lastClass === 'definitive') break;
      if (attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, delayMs * (attempt + 1)));
      }
    }
  }

  const retryFn = () => {
    clearError();
    withBootRetry(fn, opts);
  };

  setError({
    message: errorMessageForClass(lastClass),
    detail: lastError instanceof Error ? lastError.message : String(lastError),
    severity: lastClass === 'definitive' ? 'fatal' : 'retryable',
    retryFn
  });

  throw lastError;
}
